import { observer } from 'mobx-react-lite';
import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import TextareaAutosize from 'react-textarea-autosize';

import {
  _Node,
  emitOnce,
  NodeColumns,
  tableStore,
} from '../../stores/table-store';
import { getJsonOrderMap, getOrderedJson } from './utils';
import './editable-table-cell-textarea.scss';
import { runInAction, toJS } from 'mobx';

interface TableCellTextAreaProps {
  rowId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columnId?: string | number | symbol | any;
  index?: number;
  disabled?: boolean;

  onChangeConversion?: (input: string) => unknown;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderValue?: (arg: any) => string;
}

export interface MyCustomCSS extends Omit<CSSProperties, 'height'> {
  '--lines-to-show': number;
}

export const EditableTableCellTextArea: React.FC<TableCellTextAreaProps> =
  observer(
    ({
      rowId,
      columnId,
      onChangeConversion,
      renderValue,
      disabled = false,
      ...props
    }) => {
      const latestValueFromMobx = tableStore.getValue(rowId, columnId);
      const cellId = `${rowId}-${columnId}`;
      const index = tableStore.getRowIndex(rowId);
      const [inputValue, setInputValue] = useState(latestValueFromMobx);
      const [originalValueOnFocus, setOriginalValueOnFocus] =
        useState(latestValueFromMobx);
      const isJsonColumn = tableStore.isJsonColumn(rowId, columnId);
      // PSTUDIO-2154: snapshot of the cell's value (and JSON map sibling)
      // captured at focus so a committed edit can be registered as one
      // undo/redo command on blur.
      const beforeEditSnapshotRef = useRef<Record<string, unknown>>({});

      useEffect(() => {
        if (inputValue === undefined) return;

        if (inputValue !== latestValueFromMobx) {
          setInputValue(latestValueFromMobx);
        }
      }, [inputValue, latestValueFromMobx, cellId]);

      const [isFocused, setFocus] = useState(false);

      const valueToRender = useCallback(
        (value: string | object | null | undefined) => {
          if (value === null || value === undefined) return '';

          if (renderValue) {
            return renderValue(value) as string;
          }
          if (typeof value === 'object') {
            return isJsonColumn
              ? getOrderedJson({ value, rowId, columnId, stringify: true })
              : JSON.stringify(value, null, 2);
          }
          return String(value);
        },
        [renderValue, isJsonColumn, columnId, rowId]
      );

      const onFocus = () => {
        runInAction(() => {
          emitOnce(() => {
            if (
              tableStore.editingCellId !== cellId &&
              !tableStore.pendingEditCellId
            ) {
              tableStore.pendingEditCellId = cellId;
            }
            tableStore.isCellBeingEdited = true;
            tableStore.editingCellId = cellId;
            tableStore.editingRowId = rowId;
            tableStore.editingColumnId = columnId;
          });
        });
        setFocus(true);
        // Capture the value when user focuses to detect real changes
        setOriginalValueOnFocus(inputValue);
        // Snapshot the committed store value (and JSON map sibling) at focus so
        // an undo/redo command can restore it exactly on blur (PSTUDIO-2154).
        beforeEditSnapshotRef.current = {
          [columnId]: toJS(tableStore.getValue(rowId, columnId)),
          ...(isJsonColumn && {
            [`${columnId}Map`]: toJS(
              tableStore.getValue(
                rowId,
                `${columnId}Map` as Parameters<typeof tableStore.getValue>[1]
              )
            ),
          }),
        };
      };

      // When the input is blurred, we'll call our table meta's updateData function
      const onBlur = useCallback(
        (e: React.FocusEvent<HTMLTextAreaElement>) => {
          let { value } = e.target;
          setFocus(false);

          // Strip trailing \n from single-line values; preserve them for true multi-line content
          const stripped = value.replace(/\n+$/, '');
          if (!stripped.includes('\n') && stripped !== value) {
            value = stripped;
            setInputValue(value);

            const valueMap = isJsonColumn
              ? getJsonOrderMap({ value, columnId })
              : null;

            tableStore.updateRow({
              rowId,
              updates: {
                [columnId]: value,
                ...(valueMap && { [`${columnId}Map`]: valueMap }),
              },
              skipValidationRules: true,
              saveImmediately: false,
            });
          }

          setTimeout(() => {
            runInAction(() => {
              emitOnce(() => {
                tableStore.isCellBeingEdited = false;
                if (tableStore.editingCellId === cellId) {
                  tableStore.editingCellId = null;
                  tableStore.editingRowId = null;
                  tableStore.editingColumnId = null;
                }
              });
            });
          }, 100);

          const currentMobxValue = tableStore.getValue(rowId, columnId);
          const settings = tableStore.getValue(rowId, NodeColumns.settings);

          // Only save if value actually changed from when user focused
          // This prevents saving when user types then reverts back
          if (currentMobxValue !== originalValueOnFocus) {
            tableStore.saveUnsavedChanges();
            // In manual validation mode, clear the error for this cell when data was changed
            tableStore.clearCellValidationError(
              rowId,
              columnId as keyof typeof NodeColumns
            );
            // Register the committed edit as a single undo/redo command
            // (PSTUDIO-2154). The save above persists the current value; the
            // command only flips between before/after on undo/redo.
            tableStore.commitCellEdit({
              rowId,
              columnId,
              before: beforeEditSnapshotRef.current,
            });
          } else {
            // Value reverted to original - just clear dirty flag without saving
            tableStore.clearRowDirty(rowId);
          }

          // Handle task rows
          if (settings?.taskId && tableStore.isTaskRow(settings?.taskId)) {
            tableStore.updateSolutionForTaskRow(settings?.taskId, {
              [columnId]: value,
            });
          }
        },
        [rowId, columnId, originalValueOnFocus, isJsonColumn, cellId]
      );

      const textAreaRef = useRef<HTMLTextAreaElement>(null);

      const { pendingEditCellId } = tableStore;

      useLayoutEffect(() => {
        if (pendingEditCellId === cellId && textAreaRef.current && !disabled) {
          runInAction(() => {
            tableStore.pendingEditCellId = null;
          });
          const textarea = textAreaRef.current;
          textarea.focus();

          requestAnimationFrame(() => {
            const len = (textarea.value ?? '').length;
            textarea.setSelectionRange(len, len);
          });
        }
      }, [pendingEditCellId, cellId, disabled]);

      const inputValueToRender = valueToRender(inputValue);

      const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.nativeEvent.isComposing) return;

        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          e.stopPropagation();

          const textarea = e.currentTarget;
          const start = textarea.selectionStart;
          const end = textarea.selectionEnd;
          const currentValue = textarea.value;
          const newValue =
            currentValue.substring(0, start) +
            '\n' +
            currentValue.substring(end);

          setInputValue(newValue);

          const valueMap = isJsonColumn
            ? getJsonOrderMap({ value: newValue, columnId })
            : null;

          tableStore.updateRow({
            rowId,
            updates: {
              [columnId]: newValue,
              ...(valueMap && { [`${columnId}Map`]: valueMap }),
            },
            skipValidationRules: true,
            saveImmediately: false,
          });
          tableStore.markRowDirty(rowId);

          requestAnimationFrame(() => {
            textarea.selectionStart = textarea.selectionEnd = start + 1;
          });
        }
      };

      const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const { value } = e.target;
        setInputValue(value);

        const valueMap = isJsonColumn
          ? getJsonOrderMap({ value, columnId })
          : null;

        // Update MobX store immediately (but don't save to DB)
        tableStore.updateRow({
          rowId,
          updates: {
            [columnId]: value,
            ...(valueMap && { [`${columnId}Map`]: valueMap }),
          },
          skipValidationRules: true,
          saveImmediately: false,
        });

        // Track this row as dirty
        tableStore.markRowDirty(rowId);
      };

      if (tableStore.isEditing()) {
        return (
          <TextareaAutosize
            test-id={`c-editor-table--${columnId}-${index}-textarea`}
            ref={textAreaRef}
            className={`c-editor-textarea  ${disabled ? 'c-editor-textarea--disabled' : ''}`}
            style={{
              pointerEvents:
                tableStore.editingCellId === cellId ? 'auto' : 'none',
            }}
            onKeyDown={disabled ? undefined : handleKeyDown}
            onBlur={disabled ? undefined : onBlur}
            onClick={
              disabled
                ? undefined
                : () => {
                    tableStore.isCellBeingEdited = true;
                  }
            }
            onFocus={disabled ? undefined : onFocus}
            value={inputValueToRender}
            maxRows={isFocused ? 20 : 1}
            // maxRows={20}
            onChange={disabled ? undefined : handleChange}
            readOnly={disabled}
            {...props}
          />
        );
      }

      return (
        <textarea
          className={`c-editor-textarea c-editor-textarea--readonly ${disabled ? 'c-editor-textarea--disabled' : ''}`}
          rows={1}
          readOnly
          tabIndex={-1}
          value={valueToRender(latestValueFromMobx)}
        ></textarea>
      );
    }
  );
